/*
 * Settings Messages
 *
 * This contains all the text for the Settings component.
 */
import { defineMessages } from 'react-intl';

export default defineMessages({
  header: {
    id: 'app.containers.Settings.header',
    defaultMessage: 'Settings',
  },
  editSettingsFailure: {
    id: 'app.containers.Settings.editSettingsFailure',
    defaultMessage: 'Error while saving the settings, please try again.',
  },
  editSettingsSuccess: {
    id: 'app.containers.Settings.editSettingsSuccess',
    defaultMessage: 'Settings successfully saved.',
  },
  saveButton: {
    id: 'app.containers.Settings.saveButton',
    defaultMessage: 'Save Settings',
  },
  isVehicleWorkflowActive: {
    id: 'app.containers.Settings.isVehicleWorkflowActive',
    defaultMessage: 'Enable vehicle workflow for applications',
  },
  provideTeamInfoToAllParticipants: {
    id: 'app.containers.Settings.provideTeamInfoToAllParticipants',
    defaultMessage: 'Share team info with all team participants',
  },
  notifyFollowingTurnLeadersOnCancellation: {
    id: 'app.containers.Settings.notifyFollowingTurnLeadersOnCancellation',
    defaultMessage: 'Notify following turn leaders on turn cancellation',
  },
  allowTurnLeaderToCancelTurn: {
    id: 'app.containers.Settings.allowTurnLeaderToCancelTurn',
    defaultMessage: 'Allow turn leaders to cancel turn',
  },
  allowPublishersToMaintainRoutePreferences: {
    id: 'app.containers.Settings.allowPublishersToMaintainRoutePreferences',
    defaultMessage: 'Allow publishers to maintain route preferences',
  },
  provideTeamInfoToAllParticipantsOfPrecedingAndFollowingShifts: {
    id: 'app.containers.Settings.provideTeamInfoToAllParticipantsOfPrecedingAndFollowingShifts',
    defaultMessage:
      'Provide team info to all participants of preceding and following shifts',
  },
  isExperiencesFeatureEnabled: {
    id: 'app.containers.Settings.isExperiencesFeatureEnabled',
    defaultMessage: 'Publishers can report experiences',
  },
  planning: {
    id: 'app.containers.Settings.planning',
    defaultMessage: 'Planning settings',
  },
  planningSchedule: {
    id: 'app.containers.Settings.planningSchedule',
    defaultMessage: 'Automatic Planning Schedule',
  },
  news: {
    id: 'app.containers.Settings.news',
    defaultMessage: 'News settings',
  },
  reporting: {
    id: 'app.containers.Settings.reporting',
    defaultMessage: 'Reporting settings',
  },
  project: {
    id: 'app.containers.Settings.project',
    defaultMessage: 'Global settings',
  },
  participation: {
    id: 'app.containers.Settings.participation',
    defaultMessage: 'Participation settings',
  },
  deleteExpiredNewsAfterDays: {
    id: 'app.containers.Settings.deleteExpiredNewsAfterDays',
    defaultMessage: 'Delete expired news after days',
  },
  features: {
    id: 'app.containers.Settings.features',
    defaultMessage: 'Features',
  },
  assignmentRate: {
    id: 'app.containers.Settings.assignmentRate',
    defaultMessage: 'Assignment Rate',
  },
  assignmentRateStrategy: {
    id: 'app.containers.Settings.assignmentRateStrategy',
    defaultMessage: 'Calculation mode: The assignment rate is 100% when...',
  },
  assignmentRateStrategyActualApplications: {
    id: 'app.containers.Settings.assignmentRateStrategyActualApplications',
    defaultMessage: '...all applications of a publisher are assigned to turns',
  },
  assignmentRateStrategyRecommendedAssignments: {
    id: 'app.containers.Settings.assignmentRateStrategyRecommendedAssignments',
    defaultMessage:
      '...a publisher is assigned 0.5 times a week and a publisher 2 times a week',
  },
  timeFrameForAssignmentRate: {
    id: 'app.containers.Settings.timeFrameForAssignmentRate',
    defaultMessage: 'Considered time frame',
  },
  showAssignmentsWithinLastWeeks: {
    id: 'app.containers.Settings.showAssignmentsWithinLastWeeks',
    defaultMessage: 'Number of past weeks',
  },
  showAssignmentsWithinNextWeeks: {
    id: 'app.containers.Settings.showAssignmentsWithinNextWeeks',
    defaultMessage: 'Number of future weeks',
  },
  assignmentIssuesSeverities: {
    id: 'app.containers.Settings.assignmentIssuesSeverities',
    defaultMessage: 'Assignment issues severities',
  },
  assignmentIssueOptionError: {
    id: 'app.containers.Settings.assignmentIssueOptionError',
    defaultMessage: 'Error',
  },
  assignmentIssueOptionWarning: {
    id: 'app.containers.Settings.assignmentIssueOptionWarning',
    defaultMessage: 'Warning',
  },
  assignmentIssueOptionInformation: {
    id: 'app.containers.Settings.assignmentIssueOptionInformation',
    defaultMessage: 'Information',
  },
  assignmentIssueOptionNone: {
    id: 'app.containers.Settings.assignmentIssueOptionNone',
    defaultMessage: 'None',
  },
  multipleTurnLeadersSeverity: {
    id: 'app.containers.Settings.multipleTurnLeadersSeverity',
    defaultMessage: 'Multiple turn leaders assigned to one route',
  },
  noSpeakerAssignedSeverity: {
    id: 'app.containers.Settings.noSpeakerAssignedSeverity',
    defaultMessage:
      'None of the assigned publishers speaks the language for which the route is intended',
  },
  emptyTeamSeverity: {
    id: 'app.containers.Settings.emptyTeamSeverity',
    defaultMessage: 'No publisher is assigned at all',
  },
  separatedFromPartnerSeverity: {
    id: 'app.containers.Settings.separatedFromPartnerSeverity',
    defaultMessage: 'Partners are not assigned together',
  },
  applicationForMultipleShiftsNotRespectedSeverity: {
    id: 'app.containers.Settings.applicationForMultipleShiftsNotRespectedSeverity',
    defaultMessage: 'An application for multiple shifts is not respected',
  },
  consecutiveAssignmentsOnDifferentRoutesSeverity: {
    id: 'app.containers.Settings.consecutiveAssignmentsOnDifferentRoutesSeverity',
    defaultMessage:
      'A publisher is assigned on a different route in a consecutive shift',
  },
  noBackupTurnLeaderSeverity: {
    id: 'app.containers.Settings.noBackupTurnLeaderSeverity',
    defaultMessage: 'No backup team leader assigned',
  },
  teamTooSmallSeverity: {
    id: 'app.containers.Settings.teamTooSmallSeverity',
    defaultMessage: 'Fewer publishers than the minimum are assigned',
  },
  teamTooLargeSeverity: {
    id: 'app.containers.Settings.teamTooLargeSeverity',
    defaultMessage: 'More publishers than the maximum are assigned',
  },
  noVehicleAvailableSeverity: {
    id: 'app.containers.Settings.noVehicleAvailableSeverity',
    defaultMessage: 'No assigned publisher has a vehicle available',
  },
  malePublishersNeededSeverity: {
    id: 'app.containers.Settings.malePublishersNeededSeverity',
    defaultMessage: 'Not enough male publishers assigned',
  },
  routePreferenceNotRespectedSeverity: {
    id: 'app.containers.Settings.routePreferenceNotRespectedSeverity',
    defaultMessage: 'Assignment to disfavored route',
  },
  doubleShiftAssignedSeverity: {
    id: 'app.containers.Settings.doubleShiftAssignedSeverity',
    defaultMessage: 'Publisher is assigned on two consecutive shifts',
  },
  notEnoughMentorsAssignedSeverity: {
    id: 'app.containers.Settings.notEnoughMentorsAssignedSeverity',
    defaultMessage: 'Not enough mentors are assigned',
  },
  multipleShiftsOnSameDayAssignedSeverity: {
    id: 'app.containers.Settings.multipleShiftsOnSameDayAssignedSeverity',
    defaultMessage: 'Publisher assigned several times the same day',
  },
  enableScheduledPlanning: {
    id: 'app.containers.Settings.enableScheduledPlanning',
    defaultMessage: 'Schedule automatic planning',
  },
  advanceInitialPlanning: {
    id: 'app.containers.Settings.advanceInitialPlanning',
    defaultMessage: 'Advance for planning (in days)',
  },
  weeksToPlan: {
    id: 'app.containers.Settings.weeksToPlan',
    defaultMessage: 'Number of weeks to plan',
  },
  routesToPlan: {
    id: 'app.containers.Settings.routesToPlan',
    defaultMessage: 'Routes to plan',
  },
  waitWhileLoadingRoutes: {
    id: 'app.containers.Settings.waitWhileLoadingRoutes',
    defaultMessage: 'Please wait while loading routes ...',
  },
  projectName: {
    id: 'app.containers.Settings.projectName',
    defaultMessage:
      'Proejct name as it appears in the application, e.g. on the dashboard',
  },
  german: {
    id: 'app.containers.Settings.german',
    defaultMessage: 'German',
  },
  english: {
    id: 'app.containers.Settings.english',
    defaultMessage: 'English',
  },
  french: {
    id: 'app.containers.Settings.french',
    defaultMessage: 'French',
  },
  spanish: {
    id: 'app.containers.Settings.spanish',
    defaultMessage: 'Spanish',
  },
  russian: {
    id: 'app.containers.Settings.russian',
    defaultMessage: 'Russian',
  },
  smtp: {
    id: 'app.containers.Settings.smtp',
    defaultMessage: 'Email settings',
  },
  isEmailEnabled: {
    id: 'app.containers.Settings.isEmailEnabled',
    defaultMessage: 'Enable email',
  },
  emailSenderName: {
    id: 'app.containers.Settings.emailSenderName',
    defaultMessage: 'Sender name',
  },
  emailSenderAddress: {
    id: 'app.containers.Settings.emailSenderAddress',
    defaultMessage: 'Sender email address',
  },
  smtpMessageFooter: {
    id: 'app.containers.Settings.smtpMessageFooter',
    defaultMessage: 'Default email footer',
  },
  additionalBccRecipient: {
    id: 'app.containers.Settings.additionalBccRecipient',
    defaultMessage: 'Additional BCC recipient',
  },
  overriddenSmtpRecipient: {
    id: 'app.containers.Settings.overriddenSmtpRecipient',
    defaultMessage: 'Fixed recipient (for testing if emails get sent)',
  },
  none: {
    id: 'app.containers.Settings.none',
    defaultMessage: 'None',
  },
  ssl: {
    id: 'app.containers.Settings.ssl',
    defaultMessage: 'SSL',
  },
  masterData: {
    id: 'app.containers.Settings.masterData',
    defaultMessage: 'Master data',
  },
  defaultRegionCallingCode: {
    id: 'app.containers.Settings.defaultRegionCallingCode',
    defaultMessage: 'Default country calling code',
  },
  publishersAreAtRiskToBecomeInactiveAfterMonths: {
    id: 'app.containers.Settings.publishersAreAtRiskToBecomeInactiveAfterMonths',
    defaultMessage: 'Inform publishers about inactivity after months',
  },
  publishersBecomeInactiveAfterMonths: {
    id: 'app.containers.Settings.publishersBecomeInactiveAfterMonths',
    defaultMessage: 'Publishers become inactive after months',
  },
  contactInfo: {
    id: 'app.containers.Settings.contactInfo',
    defaultMessage: 'Contact info',
  },
});
